/**
 * Debounce a changing value (ADR-038 Phase F; D14).
 *
 * The Query panel debounces the *request* rather than the keystroke: the
 * predicate tree updates on every edit so the chips stay responsive, while
 * the value that becomes the query key settles only once the operator
 * pauses. Debouncing the input instead would make typing feel laggy and
 * would still fire a request per settled character.
 *
 * The first value passes through immediately — a panel that has just
 * mounted with a valid default query should run it now, not 400 ms from
 * now. "First" means the first value there is something to ask about: a
 * caller that mounts with ``null`` has not produced one yet (the Query
 * panel has no selected type until its catalog resolves, so its request is
 * ``null`` for the first few frames), and spending the pass-through on that
 * placeholder would put the opening query behind the full debounce. Both
 * REQ-UI-117 and feature spec D14 scope the 400 ms to a predicate EDIT.
 *
 * Cross-references:
 *   - REQ-UI-117 / D14 (debounced, validity-gated auto-run).
 */
import { useEffect, useRef, useState } from "react";

/**
 * Return ``value`` delayed by ``delayMs``, collapsing bursts of changes
 * into the last one.
 *
 * Equality is by serialized identity rather than reference, so a caller
 * that rebuilds an equal object every render (which the panel does — the
 * request object is derived, not memoised) does not restart the timer
 * forever and never settle.
 */
export function useDebounced<T>(value: T, delayMs: number): T {
  const [settled, setSettled] = useState(value);
  const serialized = JSON.stringify(value ?? null);
  const lastSerialized = useRef(serialized);
  const primed = useRef(false);

  useEffect(() => {
    if (!primed.current) {
      // First pass: emit immediately so a panel opening on a valid default
      // query runs it now rather than after the debounce. There is no burst
      // to collapse yet, so every value passes straight through until one
      // arrives that is actually a query — priming on a ``null`` placeholder
      // would spend the pass-through on it and delay the real opening query
      // by the full ``delayMs`` (REQ-UI-117 / D14 debounce EDITS, not open).
      primed.current = value !== null && value !== undefined;
      lastSerialized.current = serialized;
      setSettled(value);
      return;
    }
    if (serialized === lastSerialized.current) return;
    const timer = setTimeout(() => {
      lastSerialized.current = serialized;
      setSettled(value);
    }, delayMs);
    return () => clearTimeout(timer);
    // ``serialized`` is the identity of ``value``; depending on ``value``
    // itself would restart the timer on every render for a derived object.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialized, delayMs]);

  return settled;
}
